import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const paused = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!paused.current) setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  const go = (dir: number) => setIndex((i) => (i + dir + items.length) % items.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const active = items[index];
  if (!active) return null;

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] overflow-hidden"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="精选影视轮播"
    >
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={movie.poster}
            alt={`${movie.title} 精选海报`}
            title={`${movie.title} - 午夜视频精选`}
            className={`w-full h-full object-cover object-top ${i === index ? 'animate-slow-zoom' : ''}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />

      <div className="relative z-10 h-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl">
          <h1 className="inline-flex items-center gap-2 rounded-full bg-primary-500/15 border border-primary-500/40 px-4 py-1.5 text-sm font-semibold text-primary-300">
            <i className="ri-moon-clear-fill"></i>
            午夜视频 · Midnight Video
          </h1>
          <p className="mt-5 text-sm md:text-base text-secondary-300 font-medium">
            {active.typeLabel} · {active.region} · {active.year} · {active.duration}
          </p>
          <h2 key={active.id} className="mt-2 font-heading text-3xl md:text-5xl font-bold text-foreground-950 animate-fade-up">
            {active.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-primary-400 font-bold">
              <i className="ri-star-fill"></i>
              {active.rating.toFixed(1)}
            </span>
            {active.genres.map((g) => (
              <span key={g} className="px-2.5 py-0.5 rounded-full bg-background-200/70 text-secondary-300 text-xs">
                {g}
              </span>
            ))}
          </div>
          <p key={`${active.id}-desc`} className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-foreground-600 line-clamp-2 animate-fade-in">
            {active.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors"
            >
              <i className="ri-play-fill text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100/80 border border-background-300 text-foreground-900 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-apps-2-line"></i>
              更多同类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-background-100/70 border border-background-300 text-foreground-900 cursor-pointer hover:bg-primary-500 hover:text-background-950 transition-colors"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-background-100/70 border border-background-300 text-foreground-900 cursor-pointer hover:bg-primary-500 hover:text-background-950 transition-colors"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {items.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-300 hover:bg-secondary-600'
            }`}
          />
        ))}
      </div>
    </section>
  );
}